import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Activity,
  Bot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  Package,
  Settings,
  ShoppingCart,
  Store,
  Target,
  Users,
} from "lucide-react";
import { useSidebar } from "@/context/SidebarContext";
import { useClerk } from "@clerk/clerk-react";
import { TenantSwitcher } from "./TenantSwitcher";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  match?: (path: string) => boolean;
}

export function Sidebar() {
  const { collapsed, toggle } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const [storeOpen, setStoreOpen] = useState(
    () => location.pathname.startsWith("/store"),
  );

  const topNav: NavItem[] = [
    { to: "/", icon: LayoutDashboard, label: "Cockpit", match: (p) => p === "/" },
    { to: "/agents", icon: Bot, label: "Agents", match: (p) => p.startsWith("/agents") },
    { to: "/goals", icon: Target, label: "Goals", match: (p) => p.startsWith("/goals") },
  ];

  const storeNav: NavItem[] = [
    { to: "/store/traffic", icon: Activity, label: "Traffic", match: (p) => p === "/store/traffic" },
    { to: "/store/revenue", icon: ShoppingCart, label: "Revenue", match: (p) => p === "/store/revenue" },
    { to: "/store/products", icon: Package, label: "Products", match: (p) => p === "/store/products" },
    { to: "/store/customers", icon: Users, label: "Customers", match: (p) => p === "/store/customers" },
  ];

  const isActive = (item: NavItem) =>
    item.match ? item.match(location.pathname) : location.pathname === item.to;

  const storeActive = location.pathname.startsWith("/store");

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r border-border bg-card transition-[width] duration-150 ease-out",
        collapsed ? "w-[52px]" : "w-[220px]",
      )}
    >
      {/* Header: tenant switcher + collapse toggle */}
      <div className="flex h-12 items-center gap-1 border-b border-border px-2">
        <div className="min-w-0 flex-1">
          <TenantSwitcher collapsed={collapsed} />
        </div>
        <button
          onClick={toggle}
          className={cn(
            "inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors",
            collapsed && "mx-auto",
          )}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {topNav.map((item) => (
          <NavLink key={item.to} item={item} active={isActive(item)} collapsed={collapsed} />
        ))}

        {/* Store group */}
        {collapsed ? (
          <Link
            to="/store/traffic"
            className={cn(
              "flex items-center justify-center rounded-md py-2 text-sm transition-colors",
              storeActive
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
            )}
            title="Store"
          >
            <Store className="h-4 w-4 shrink-0" />
          </Link>
        ) : (
          <>
            <button
              onClick={() => setStoreOpen((o) => !o)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                storeActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
              )}
            >
              <Store className="h-4 w-4 shrink-0" />
              <span className="truncate">Store</span>
              <ChevronDown
                className={cn(
                  "ml-auto h-3.5 w-3.5 transition-transform",
                  storeOpen && "rotate-180",
                )}
              />
            </button>
            {storeOpen && (
              <div className="ml-4 space-y-0.5 border-l border-border/50 pl-2">
                {storeNav.map((item) => (
                  <NavLink key={item.to} item={item} active={isActive(item)} collapsed={false} sub />
                ))}
              </div>
            )}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-border px-2 py-2 space-y-0.5">
        <Link
          to="/settings"
          className={cn(
            "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground",
            location.pathname === "/settings" && "bg-accent text-foreground",
            collapsed && "justify-center px-0",
          )}
          title={collapsed ? "Settings" : undefined}
        >
          <Settings className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>
        <button
          onClick={() => {
            void signOut(() => navigate("/login", { replace: true }));
          }}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground",
            collapsed && "justify-center px-0",
          )}
          title={collapsed ? "Sign out" : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  );
}

function NavLink({
  item,
  active,
  collapsed,
  sub,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  sub?: boolean;
}) {
  return (
    <Link
      to={item.to}
      className={cn(
        "flex items-center gap-2.5 rounded-md text-sm transition-colors",
        sub ? "px-2 py-1.5" : "px-2.5 py-2",
        active
          ? "bg-accent text-foreground"
          : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
        collapsed && "justify-center px-0",
      )}
      title={collapsed ? item.label : undefined}
    >
      <item.icon className={cn("shrink-0", sub ? "h-3.5 w-3.5" : "h-4 w-4")} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}
